import { RouteLocationNormalized } from 'vue-router'
import {
  AppNavigationGuard,
  AppRouteMeta,
  createMfWrapper,
} from '@sennder/shell-utilities'

import router, { DEFAULT_ROUTE } from '@/router'
import { logger } from '@/services/logger/loggers'
import { getRouteLocationName } from '@/modules/utils'
import { AppRoute, routes } from '@/config/routes'
import { authRoutes } from '@/modules/auth/index'
import { i18n } from '@/services/i18n'
import { auth } from '@/modules/auth'
import { hasLimitedFacilities } from '@/config/user-access'

const OPERATIONS_OVERVIEW_ROUTE = '/overview'

const routeNavigationGuard: AppNavigationGuard = async (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized
) => {
  const authGuards = to.meta.public ? [] : [isAuthenticatedGuard]
  const guards = [...authGuards, ...(to.meta.guards || [])]

  // Users limited to a set of facilities may only reach the Operations Overview.
  // Any other authenticated route is redirected there.
  if (
    !to.meta.public &&
    to.path !== OPERATIONS_OVERVIEW_ROUTE &&
    hasLimitedFacilities()
  ) {
    return { path: OPERATIONS_OVERVIEW_ROUTE }
  }

  if (guards.length === 0) {
    return true
  }

  for (const guard of guards) {
    const result = await guard(to, from)
    if (
      typeof result === 'object' ||
      typeof result === 'string' ||
      result === false
    ) {
      return result
    }
  }

  return true
}

const setPageTitle = (name: string): void => {
  const key = `navigation.item.${name}`
  const pageName = i18n.global.t(key)
  document.title = `${pageName} |  Shipper Portal - sennder`
}

const addAppRoute = (routePath: AppRoute, routeMeta: AppRouteMeta) => {
  const component = createMfWrapper({
    id: routeMeta.name,
    renderer: routeMeta.renderer,
    hooks: {
      failure: logger.error,
    },
    router,
  })

  const guards = routeMeta.guards || []

  router.addRoute({
    path: routePath,
    name: routeMeta.name,
    component: () => component,
    meta: {
      public: routeMeta.public,
      fullscreenLayout: routeMeta.fullscreenLayout,
      guards,
    },
    children: [
      {
        path: ':catchAll(.*)',
        meta: {
          guards,
        },
        component,
      },
    ],
  })
}

const registerRoutes = () => {
  initRouteGuards()
  for (const route of authRoutes) {
    router.addRoute(route)
  }

  for (const [path, route] of Object.entries(routes)) {
    addAppRoute(path as AppRoute, route)
  }

  router.addRoute({
    path: '/:catchAll(.*)',
    name: 'not-found',
    meta: {
      public: true,
      fullscreenLayout: true,
    },
    component: () => import('../NotFound.vue'),
  })

  router.addRoute({
    path: '/',
    redirect: { path: DEFAULT_ROUTE },
  })
}

const initRouteGuards = () => {
  router.beforeEach((to, from) => {
    return routeNavigationGuard(to, from)
  })

  router.beforeResolve((to) => {
    const name = getRouteLocationName(to)
    setPageTitle(String(name))
  })
}

const isAuthenticatedGuard: AppNavigationGuard = async (
  to: RouteLocationNormalized
) => {
  const isAuthenticated = !!(await auth.value?.getAuthToken())
  if (isAuthenticated) {
    return true
  } else {
    return {
      path: '/login',
      query: { redirectTo: encodeURIComponent(to.fullPath) },
    }
  }
}

export const initRouter = () => {
  registerRoutes()
  initRouteGuards()
}
